import { MILL_1 } from "@/setup/mill-config/mill-config";
import { ProductionEventSelector } from "@/shared/selectors/ProductionEventSelector";
import { SectionSelector } from "@/shared/selectors/SectionSelector";
import { SurfaceDefectSelector } from "@/shared/selectors/SurfaceDefectSelector";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
	Autocomplete,
	Box,
	Button,
	Chip,
	Grid2,
	IconButton,
	TextField,
	Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers";
import type { ReactNode } from "react";
import {
	HeatOption,
	ProductionEventOption,
} from "../../models/InspectionReport";
import { InspectionReportFilters } from "../InspectionsReportPage";
import {
	DATE_PRESETS,
	getPresetRange,
	isLatestDateRange,
	shiftDateRange,
} from "../utils/period";
import { RestartAltOutlined } from "@mui/icons-material";

const heatLabel = (heat: HeatOption) =>
	heat.GradeName ? `${heat.HeatName} · ${heat.GradeName}` : heat.HeatName;

type Props = {
	view: "table" | "dashboard";
	filters: InspectionReportFilters;
	events: ProductionEventOption[];
	heats: HeatOption[];
	isLoadingOptions: boolean;
	changedFilters: number;
	actions?: ReactNode;
	onChange: (updates: Partial<InspectionReportFilters>) => void;
	onRestore: () => void;
};

export const InspectionsReportFilters = ({
	view,
	filters,
	events,
	heats,
	isLoadingOptions,
	changedFilters,
	actions,
	onChange,
	onRestore,
}: Props) => {
	const isDashboard = view === "dashboard";
	const activePreset = filters.datePreset;

	const canNavigate = filters.startDate !== null && filters.endDate !== null;

	const navigate = (direction: 1 | -1) => {
		if (filters.startDate === null || filters.endDate === null) return;

		const range = shiftDateRange(
			filters.startDate,
			filters.endDate,
			direction,
			activePreset,
		);

		onChange({
			startDate: range.start,
			endDate: range.end,
			datesPinned: filters.datesPinned,
		});
	};

	return (
		<>
			<Box
				sx={{
					alignItems: "center",
					display: "flex",
					flexWrap: "wrap",
					gap: 0.75,
					mb: 1.25,
				}}
			>
				{DATE_PRESETS.map((preset) => {
					const isActive = preset.value === activePreset;

					return (
						<Chip
							key={preset.value}
							label={preset.label}
							clickable
							aria-pressed={isActive}
							sx={{
								backgroundColor: isActive
									? (theme) => alpha(theme.palette.primary.main, 0.12)
									: "background.paper",
								border: 1,
								borderColor: isActive
									? (theme) => alpha(theme.palette.primary.main, 0.5)
									: "divider",
								borderRadius: 999,
								color: isActive ? "primary.main" : "text.secondary",
								fontSize: "0.8125rem",
								fontWeight: isActive ? 600 : 500,
								height: 30,
								px: 0.5,
								transition:
									"background-color 120ms, border-color 120ms, color 120ms",
								"&:hover": {
									backgroundColor: isActive
										? (theme) => alpha(theme.palette.primary.main, 0.18)
										: "action.hover",
									borderColor: isActive ? "primary.main" : "text.disabled",
									color: isActive ? "primary.main" : "text.primary",
								},
							}}
							onClick={() => {
								const range = getPresetRange(preset.value);

								onChange({
									startDate: range.start,
									endDate: range.end,
									datesPinned: false,
									datePreset: preset.value,
								});
							}}
						/>
					);
				})}
			</Box>
			<Grid2 container spacing={1} alignItems="center">
				<Grid2 size="auto">
					<Tooltip title="Previous period">
						<span>
							<IconButton
								size="small"
								aria-label="Previous period"
								disabled={!canNavigate}
								onClick={() => navigate(-1)}
								sx={{ backgroundColor: "background.paper" }}
							>
								<ChevronLeftIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
				</Grid2>
				<Grid2 size={{ md: 1.3, xs: 5 }}>
					<DatePicker
						label="Start"
						format="MM/DD/YYYY"
						value={filters.startDate}
						onChange={(value) => onChange({ startDate: value })}
						slotProps={{
							field: { clearable: true },
							textField: {
								size: "small",
								fullWidth: true,
								sx: { backgroundColor: "background.paper" },
							},
						}}
					/>
				</Grid2>
				<Grid2 size={{ md: 1.3, xs: 5 }}>
					<DatePicker
						label="End"
						format="MM/DD/YYYY"
						value={filters.endDate}
						onChange={(value) => onChange({ endDate: value })}
						slotProps={{
							field: { clearable: true },
							textField: {
								size: "small",
								fullWidth: true,
								sx: { backgroundColor: "background.paper" },
							},
						}}
					/>
				</Grid2>
				<Grid2 size="auto">
					<Tooltip title="Next period">
						<span>
							<IconButton
								size="small"
								aria-label="Next period"
								disabled={
									!canNavigate ||
									(filters.endDate !== null &&
										isLatestDateRange(filters.endDate))
								}
								onClick={() => navigate(1)}
								sx={{ backgroundColor: "background.paper" }}
							>
								<ChevronRightIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
				</Grid2>
				<Grid2 size={{ md: 1, xs: 12 }}>
					<SectionSelector
						label="Section"
						value={filters.sectionId}
						size="small"
						assetId={MILL_1.id}
						hideTph
						sortBy="recent"
						onChange={(sectionId, sectionName) =>
							onChange({
								sectionId,
								sectionName: sectionName ?? null,
								eventId: null,
								heatId: null,
								heatName: null,
							})
						}
					/>
				</Grid2>
				<Grid2 size={{ md: 3, xs: 12 }}>
					<ProductionEventSelector
						label="Event"
						options={events}
						value={filters.eventId}
						showSection={filters.sectionId === null}
						isLoading={isLoadingOptions}
						emptyText="There are no events for this section."
						// On the dashboard a section reads one of its runs: with one picked,
						// there is no "All" to fall back to.
						allowAll={!isDashboard || filters.sectionId === null}
						onChange={(event) =>
							onChange(
								event === null
									? { eventId: null }
									: {
											eventId: event.EventId,
											sectionId: event.SectionId,
											sectionName: event.SectionName,
										},
							)
						}
					/>
				</Grid2>
				{!isDashboard && (
					<>
						<Grid2 size={{ md: 1.3, xs: 12 }}>
							<Autocomplete
								options={heats}
								loading={isLoadingOptions}
								size="small"
								clearOnEscape
								autoHighlight
								value={
									heats.find((heat) => heat.HeatId === filters.heatId) ?? null
								}
								getOptionLabel={(option) => option.HeatName}
								isOptionEqualToValue={(option, value) =>
									option.HeatId === value.HeatId
								}
								renderOption={(props, option) => (
									<Box component="li" {...props} key={option.HeatId}>
										{heatLabel(option)}
									</Box>
								)}
								onChange={(_event, option) =>
									onChange({
										heatId: option?.HeatId ?? null,
										heatName: option?.HeatName ?? null,
									})
								}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Heat"
										size="small"
										sx={{ backgroundColor: "background.paper" }}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ md: 2.5, xs: 12 }}>
							<SurfaceDefectSelector
								label="Defect type"
								value={filters.surfaceDefectIds}
								onChange={(surfaceDefectIds, defects) =>
									onChange({
										surfaceDefectIds,
										surfaceDefectNames: defects.map(
											(defect) => defect.SurfaceDefectName,
										),
									})
								}
							/>
						</Grid2>
					</>
				)}
				<Grid2
					size={{ md: "grow", xs: 12 }}
					container
					alignItems="center"
					justifyContent={isDashboard ? "flex-end" : "center"}
					spacing={1}
				>
					{actions}
					<Button
						disabled={changedFilters === 0}
						onClick={onRestore}
						color="primary"
						variant="text"
						startIcon={<RestartAltOutlined />}
						sx={{ textTransform: "none", whiteSpace: "nowrap" }}
					>
						{changedFilters === 0
							? "Reset filters"
							: `Reset filters (${changedFilters})`}
					</Button>
				</Grid2>
			</Grid2>
		</>
	);
};
